import { memo, useMemo } from 'react';

import type { RelevancyCardProps } from '../DynamicCardComponent';
import { useRelevancyOptions } from '../store';

import BaseCardComponent from './BaseCardComponent';

import { trackCardElement } from '@services/GoogleTagManagerService';
import { getArticleUrl } from '@services/LinkRuleService';
import { useSponsor } from '@shared/RandomSponsors/useSponsor';

const defaultFallbackImg = `${window.imgBaseUrl}/imgml/uefacom/insideuefa/fallback.png`;

export const DocumentComponent = (props: RelevancyCardProps) => {
  const {
    item: { teaser },
    index,
  } = props;
  const { options } = useRelevancyOptions();

  const sponsorName = useMemo(
    () => !options.disableSponsors && (teaser.sponsor?.type ?? teaser.sponsors?.at(0)),
    [options.disableSponsors, teaser.sponsor, teaser.sponsors],
  );
  const { sponsor } = useSponsor(sponsorName);

  const isSponsoredCard = useMemo(() => !options.disableSponsors && !!sponsor, [options.disableSponsors, sponsor]);

  const trackingData = useMemo(
    () => ({
      name: [isSponsoredCard && sponsor?.code, teaser.title].filter(Boolean).join(' - '),
      id: teaser.id,
      item_category3: sponsor?.code ? 'sponsored' : undefined,
      item_category4: sponsor?.code,
    }),
    [isSponsoredCard, sponsor?.code, teaser.title, teaser.id],
  );

  const iconName = useMemo(() => {
    if (!options.iconNames || options.iconNames.length < index) {
      return null;
    }
    return options.iconNames[index];
  }, [index, options.iconNames]);

  const link = useMemo(() => teaser.targetUrl ?? getArticleUrl(teaser.id) ?? teaser.webReference, [teaser]);

  const association = useMemo(() => {
    if (!options.showAssociation) {
      return null;
    }
    const associationsRelated = teaser.relations?.filter((r) => r.association);
    if (associationsRelated && associationsRelated.length === 1)
      return associationsRelated[0].association.associationId;
    return 'UEFA';
  }, [teaser, options.showAssociation]);

  return (
    <div ref={trackCardElement(trackingData)} className='card editorial-content document-content'>
      <BaseCardComponent
        {...props}
        isDocument
        iconName={iconName}
        sponsorCode={sponsor?.code}
        title={teaser.title}
        newsDate={teaser.newsDate}
        url={link}
        image={teaser.mainMedia?.image}
        fallbackImg={defaultFallbackImg}
        association={association}
        showRoofline={options.showRoofline}
      />
    </div>
  );
};

export default memo(DocumentComponent);
